import * as React from "react";
import { Select as BaseSelect } from "react-ui";

import { AbsoluteModelPath } from "../../../../../Common/ModelPath/ModelPath";
import { PicklistDataItem, PicklistQueryResponse } from "../../../Engine/Core/Picklists/PicklistTypes";
import { MetricsValueSenderActions } from "../../Internals/Metrics/MetricsValueSender";
import { TextOverflowHint } from "../../Internals/TextOverflowHint/TextOverflowHint";
import { Tooltip } from "../../Internals/Tooltip/Tooltip";
import { AnyContext, pullContext } from "../../TypingUtils";
import { FocusManagementObserver } from "../../Services/FocusManagement/FocusManagementObserver";
import { IScrollToContext, scrollToContextTypes } from "../../Internals/Contexts/ScrollToContext";
import { ControlTooltipProps } from "../../Internals/Tooltip/ControlTooltipProps";
import { ControlValidationProps } from "../../Validations/ValidationProps";
import { TestPageObjectHandle } from "../../Services/TestPageObjects/TestPageObjectHandle";
import { ValueEditorsFocusManager } from "../../../Helpers/JumpByEnterOnNextControl/ValueEditorsFocusManager";
import { Text } from "../../ValueViewers/Text/Text";
import { ReadonlyValueWrapper } from "../ComboBox/ComboBox.styles";
import { tryScrollIntoViewWithFallback } from "../../../Helpers/BrowserHelpers";

import { HintContainer, ItemContainer, Placeholder, Root } from "./Select.styles";

type Item =
    | string
    | [string, undefined | string]
    | [string, undefined | string, undefined | string, undefined | PicklistDataItem];

interface Reference {
    code: string;
    hint: undefined | string;
    name: undefined | string;
}

export interface SelectProps extends ControlTooltipProps, ControlValidationProps {
    value: undefined | string;
    onChange: (value: string) => void;
    allowTextWrap?: boolean;
    bindingPath: AbsoluteModelPath;
    disabled?: boolean;
    disablePortal?: boolean;
    display?: "value" | "item" | "name";
    gId?: string;
    gPaths?: string[];
    isTooltipEnabled?: boolean;
    placeholder?: string;
    optional?: boolean;
    reference: Reference[];
    tooltip?: string;
    width?: string | number;
    menuWidth?: string | number;
    metrics: MetricsValueSenderActions;
    hintSource?: string;
    highlighted?: boolean;
    onQueryPicklist: () => Promise<PicklistQueryResponse>;
    onQueryOnlyGPathData: () => Promise<PicklistQueryResponse>;
    setPicklistDataItem?: (value: undefined | PicklistDataItem) => void;
    focusByEnterOnNextControl: (event: React.KeyboardEvent) => void;
    isReadOnly?: boolean;
    onClick?: () => void;
    isLink?: boolean;
    "data-tid"?: string;
}

interface State {
    items: Item[];
    suitableCount: number;
    opened: boolean;
}

export class Select extends React.Component<SelectProps, State> {
    public static defaultProps = { reference: [] };
    public static contextTypes = scrollToContextTypes;

    private readonly container = React.createRef<HTMLSpanElement>();
    private readonly select = React.createRef<BaseSelect<string, string>>();

    public constructor(props: SelectProps, context: AnyContext) {
        super(props, context);
        const items = props.reference.map<Item>(x => [x.code, x.name, x.hint, undefined]);

        this.state = {
            suitableCount: items.length,
            items: items,
            opened: false,
        };
    }

    public async componentDidMount(): Promise<void> {
        if (this.props.reference.length == 0) {
            const result = await this.props.onQueryPicklist();
            this.setItems(result.items, result.suitableCount);
            if (this.props.value != undefined) {
                const picklistDataItem = this.state.items.find(x => x[0] === this.props.value)?.[3];
                if (typeof picklistDataItem !== "string") {
                    this.props.setPicklistDataItem?.(picklistDataItem);
                }
            }
        }
    }

    public render(): JSX.Element {
        if (this.props.isReadOnly) {
            const value = this.itemToString(this.props.value);
            return (
                <TextOverflowHint text={value} width={20}>
                    <ReadonlyValueWrapper>
                        <Text
                            bindingPath={this.props.bindingPath}
                            onClick={this.props.onClick}
                            data-tid={this.props["data-tid"]}
                            value={this.itemToString(value)}
                            isLink={this.props.isLink}
                        />
                    </ReadonlyValueWrapper>
                </TextOverflowHint>
            );
        }
        const { error, tooltip, warning, isTooltipEnabled, optional, value } = this.props;

        const tooltipMessage = error || warning || tooltip;
        const tooltipTrigger = isTooltipEnabled && tooltipMessage ? "hover" : "closed";
        // Diadoc выключает портал, hint появляется при навередии на меню
        const hintEnabled = isTooltipEnabled && tooltipTrigger === "closed" && !this.props.disablePortal;

        const isNeedToInsertEmptyValue = optional && value;
        const items = isNeedToInsertEmptyValue
            ? ([[""], BaseSelect.SEP] as Array<Item | (() => JSX.Element)>).concat(this.state.items)
            : this.state.items;
        const tooltipPosition = this.props.tooltipPosition ?? "top left";

        // В общем, тут используется тайное знание о том, что в items можно передать тупл из трёх элементов
        // и третий элемент прокинется в renderItems
        /* eslint-disable @typescript-eslint/no-explicit-any */
        return (
            <TestPageObjectHandle
                id={this.props.bindingPath}
                pageObject={{
                    getType: () => "Select",
                    getValue: () => this.props.value,
                    setValue: (nextValue: string) => this.handleChange(nextValue),
                }}>
                <FocusManagementObserver onRequestFocus={this.handleRequestFocus}>
                    <Root
                        highlighted={this.props.highlighted}
                        onClick={this.handleClick}
                        data-control="select"
                        ref={this.container}>
                        <Tooltip
                            trigger={tooltipTrigger}
                            value={tooltipMessage}
                            pos={tooltipPosition}
                            width={this.props.tooltipWidth}>
                            <TextOverflowHint text={this.itemToString(this.props.value)} showHint={hintEnabled}>
                                <BaseSelect<string, string>
                                    placeholder={this.props.placeholder}
                                    disablePortal={this.props.disablePortal}
                                    disabled={this.props.disabled}
                                    data-tid={this.props["data-tid"]}
                                    error={Boolean(error)}
                                    items={items as any[]}
                                    renderItem={this.renderItem as any}
                                    renderValue={this.renderValue as any}
                                    value={value}
                                    onClose={this.handleClose}
                                    onOpen={this.handleOpen}
                                    onKeyDown={this.handleKeyDown}
                                    warning={Boolean(warning)}
                                    width={this.props.width}
                                    menuWidth={this.props.menuWidth}
                                    onValueChange={this.handleChange}
                                    onFocus={this.focusHandler}
                                    ref={this.select}
                                />
                            </TextOverflowHint>
                        </Tooltip>
                    </Root>
                </FocusManagementObserver>
            </TestPageObjectHandle>
        );
        /* eslint-enable @typescript-eslint/no-explicit-any */
    }

    private readonly focusHandler = () => {
        // @ts-ignore

        ValueEditorsFocusManager.setCurrentValueEditorOrder(event.target);
    };

    private readonly renderItem = (value: string, item: string, hint: string) => {
        if (!value && !item && !hint && this.props.optional) {
            return <div data-tid="select-reset-value">{`Сбросить значение`}</div>;
        }

        return (
            <ItemContainer allowTextWrap={this.props.allowTextWrap}>
                <span>{this.getRenderValue(value, item)}</span>
                {hint && <HintContainer>{hint}</HintContainer>}
            </ItemContainer>
        );
    };

    private readonly renderValue = (value: string, item: string): JSX.Element | undefined => {
        if (this.props.value) {
            return <span>{this.getRenderValue(value, item)}</span>;
        }
        return <Placeholder>{this.props.placeholder}</Placeholder>;
    };

    private readonly handleClick = async (): Promise<void> => {
        if (this.props.gPaths != undefined) {
            const result = await this.props.onQueryOnlyGPathData();
            this.setItems(result.items, result.suitableCount);
        }

        this.props.metrics?.saveValue(this.itemToString(this.props.value));
    };

    public readonly handleChange = (value: string) => {
        if (this.props.gId != undefined || this.props.gPaths != undefined) {
            const picklistDataItem = this.state.items.find(x => x[0] === value)?.[3];
            if (typeof picklistDataItem !== "string") {
                this.props.setPicklistDataItem?.(picklistDataItem);
            }
        }
        this.props.onChange(value);

        this.props.metrics?.sendValue(
            this.props.bindingPath,
            this.itemToString(value),
            undefined,
            this.state.suitableCount,
            undefined,
            undefined,
            "Select"
        );
    };

    private readonly handleOpen = () => {
        this.setState({ opened: true });
    };

    private readonly handleClose = () => this.setState({ opened: false });

    private readonly handleKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
        if (event.key === "Enter" && this.state.opened) {
            ValueEditorsFocusManager.focusOnNextValueEditor();
        }
    };

    private readonly handleRequestFocus = (): void => {
        if (this.container.current != undefined) {
            this.getContext().scrollTo?.(this.container.current);
            tryScrollIntoViewWithFallback(this.container.current, { block: "center", inline: "center" });
            this.container.current.focus();
        }
        this.select.current?.focus();
        this.select.current?.open();
    };

    private setItems(items: PicklistDataItem[], suitableCount: number): void {
        this.setState({
            items: items.map<Item>(item => [
                item.code?.toString() ?? "",
                item.name?.toString() ?? "",
                this.props.hintSource ? item[this.props.hintSource]?.toString() : undefined,
                item,
            ]),
            suitableCount: suitableCount,
        });
    }

    private getRenderValue(value: string | undefined, item: string | undefined): string {
        switch (this.props.display) {
            case "value":
                return value ?? "";
            case "item":
            case "name":
                return item ?? "";
            default:
                return `${value} – ${item}`;
        }
    }

    private readonly itemToString = (itemCode?: string): string => {
        if (!itemCode) {
            return "";
        }

        const item = this.state.items.find(([code]) => code === itemCode);
        return item && item[1] ? this.getRenderValue(itemCode, item[1]) : itemCode;
    };

    private getContext(): IScrollToContext {
        return pullContext<IScrollToContext>(this);
    }
}
